<template>
    <div class="provider-search-card">
        <div class="provider-search-card__body">
            <div class="provider-search-card__header">
                <div class="provider-search-card__avatar-wrap">
                    <common-user-avatar
                        :user="providerForAvatar"
                        size="user-context"
                        type="secondary"
                        :isUserProfileLinkEnabled="false"
                    ></common-user-avatar>
                </div>
                <div class="provider-search-card__header-content">
                    <p class="provider-search-card__name">
                        {{ providerDisplayName }}
                        <span v-if="isVerified" class="provider-search-card__verified">
                            {{ $t('elements.referrals.provider_search_card.verified') }}
                        </span>
                    </p>
                    <p v-if="practiceName" class="provider-search-card__practice">{{ practiceName }}</p>
                    <span v-if="isBoardCertified" class="provider-search-card__badge provider-search-card__badge--board-cert">
                        {{ $t('elements.referrals.provider_search_card.board_cert') }}
                    </span>
                </div>
                <div v-if="hasRating" class="provider-search-card__rating">
                    <common-icon name="star-filled" size="tiny"></common-icon>
                    <span>{{ (professionalRating || 0).toFixed(1) }}</span>
                </div>
            </div>

            <p v-if="specialtyName" class="provider-search-card__specialty">{{ specialtyName }}</p>

            <div v-if="locationText" class="provider-search-card__location">
                <common-icon name="location" size="tiny"></common-icon>
                <span>{{ locationText }}</span>
            </div>

            <div v-if="practiceOptions.length > 1" class="provider-search-card__practice-choice">
                <p class="provider-search-card__practice-choice-label">
                    {{ $t('elements.referrals.provider_search_card.practice_choice', { count: practiceOptions.length }) }}
                </p>
                <select-input
                    :options="practiceOptions"
                    :value="chosenPracticeUserId"
                    @change="$emit('choose-practice', Number($event))"
                ></select-input>
            </div>

            <p v-if="connectionCount != null" class="provider-search-card__connections">
                <common-icon name="users" size="tiny"></common-icon>
                {{ $t('elements.referrals.provider_search_card.connections_in_network', { count: connectionCount }) }}
            </p>
        </div>

        <div class="provider-search-card__actions">
            <common-button
                :label="isSelected ? $t('common.microcopy.selected') : $t('elements.referrals.provider_search_card.refer')"
                :type="isSelected ? 'secondary' : 'primary'"
                size="medium"
                icon="add-user"
                :isSubmittable="false"
                class="provider-search-card__refer-btn"
                @click="$emit('refer', provider)"
            ></common-button>
            <div class="provider-search-card__secondary-actions">
                <a
                    v-if="email"
                    :href="`mailto:${email}`"
                    class="provider-search-card__icon-btn"
                    :title="$t('elements.referrals.provider_search_card.message')"
                    @click="$emit('message', provider)"
                >
                    <common-icon name="message" size="tiny"></common-icon>
                </a>
                <button
                    v-if="phone"
                    type="button"
                    class="provider-search-card__icon-btn"
                    :title="$t('elements.referrals.provider_search_card.call')"
                    @click="handleCall"
                >
                    <common-icon name="call" size="tiny"></common-icon>
                </button>
            </div>
        </div>
    </div>
</template>

<script>
    import SelectInput from 'components/form-inputs/SelectInput'

    export default {
        components: { SelectInput },

        props: {
            provider: { type: Object, required: true },
            isSelected: { type: Boolean, default: false },
            // The provider's MedMatch practices that fit the search, best first (GET referrals/npi-practices).
            practices: { type: Array, default: () => [] },
            chosenPracticeUserId: { type: Number, default: null },
        },

        computed: {
            practiceOptions() {
                return this.practices.map((practice) => {
                    const place = [practice.city, practice.state].filter(Boolean).join(', ')
                    const distance =
                        practice.distanceMiles != null
                            ? this.$t('elements.referrals.provider_search_card.practice_distance', {
                                  miles: practice.distanceMiles,
                              })
                            : ''
                    return {
                        value: practice.userId,
                        label: [practice.practiceName, place, distance].filter(Boolean).join(' · '),
                    }
                })
            },

            providerForAvatar() {
                const p = this.provider
                return {
                    firstName: p.firstName || p.practice?.name?.split(' ')[0],
                    lastName: p.lastName || p.practice?.name?.split(' ').slice(1).join(' ') || '',
                }
            },

            providerDisplayName() {
                const p = this.provider
                const first = p.firstName || ''
                const last = p.lastName || ''
                const designation = p.designation || p.credential || ''
                const full = `${first} ${last}`.trim()
                if (full) {
                    return designation ? `${full}, ${designation}` : full
                }
                return p.practice?.name || ''
            },

            practiceName() {
                const p = this.provider
                if (p.practice?.name) {
                    const providerFull = `${p.firstName || ''} ${p.lastName || ''}`.trim()
                    if (p.practice.name !== providerFull) {
                        return p.practice.name
                    }
                }
                return p.organizationName || null
            },

            isVerified() {
                return !!this.provider.isVerified
            },

            isBoardCertified() {
                return !!this.provider.isBoardCertified
            },

            specialtyName() {
                const specs = this.provider.specialities || []
                const first = specs[0]
                if (!first) return null
                return first.subSpeciality?.name || first.speciality?.name || null
            },

            locationText() {
                const p = this.provider
                const loc = p.practice?.locations?.[0] || p.practice
                const city = loc?.city || p.city
                const state = loc?.state || p.state
                if (city && state) return `${city}, ${state}`
                if (city) return city
                if (state) return state
                return null
            },

            connectionCount() {
                return this.provider.connectionCount
            },

            professionalRating() {
                return this.provider.professionalRating ?? this.provider.rating
            },

            hasRating() {
                const r = this.professionalRating
                return r != null && !Number.isNaN(Number(r))
            },

            email() {
                return this.provider.email || this.provider.practice?.locations?.[0]?.email || null
            },

            phone() {
                const p = this.provider
                return p.phone || p.practice?.phone || p.practice?.locations?.[0]?.phone || null
            },
        },

        methods: {
            handleCall() {
                const tel = this.phone
                if (tel) window.location.href = `tel:${tel.replace(/\D/g, '')}`
                this.$emit('call', this.provider)
            },
        },
    }
</script>

<style lang="scss" scoped>
    @import 'styles/setup/variables';

    .provider-search-card {
        background: #fff;
        border: 1px solid $color--border-base;
        border-radius: 8px;
        padding: 16px;
        display: flex;
        flex-direction: column;
        height: 100%;

        &__body {
            display: flex;
            flex-direction: column;
            gap: 10px;
        }
    }

    .provider-search-card__header {
        display: flex;
        align-items: flex-start;
        gap: 12px;
        position: relative;
    }

    .provider-search-card__avatar-wrap {
        flex-shrink: 0;
        :deep(.avatar) {
            background: #e8e8e8 !important;
            color: #666 !important;
        }
    }

    .provider-search-card__header-content {
        flex: 1;
        min-width: 0;
    }

    .provider-search-card__name {
        font-size: 15px;
        font-weight: 600;
        color: $color--txt-header;
        margin: 0 0 2px 0;
        display: flex;
        align-items: center;
        gap: 6px;
        flex-wrap: wrap;
    }

    .provider-search-card__verified {
        font-size: 11px;
        font-weight: 500;
        color: #13b3e3;
        background: transparent;
    }

    .provider-search-card__practice {
        font-size: 13px;
        color: $color--txt-base;
        margin: 0 0 6px 0;
    }

    .provider-search-card__badge {
        display: inline-block;
        font-size: 11px;
        font-weight: 500;
        padding: 2px 8px;
        border-radius: 12px;

        &--board-cert {
            background: #e8f5e9;
            color: #2e7d32;
        }
    }

    .provider-search-card__rating {
        position: absolute;
        top: 0;
        right: 0;
        display: flex;
        align-items: center;
        gap: 4px;
        font-size: 13px;
        font-weight: 600;
        color: $color--txt-header;

        .icon {
            color: #f5a623;
        }
    }

    .provider-search-card__specialty {
        font-size: 14px;
        font-weight: 700;
        color: $color--txt-header;
        margin: 0;
    }

    .provider-search-card__location,
    .provider-search-card__practice-choice {
        display: flex;
        flex-direction: column;
        gap: 6px;
    }

    .provider-search-card__practice-choice-label {
        font-size: 13px;
        color: $color--txt-base;
        margin: 0;
    }

    .provider-search-card__connections {
        display: flex;
        align-items: center;
        gap: 6px;
        font-size: 13px;
        color: $color--txt-base;
        margin: 0;
    }

    .provider-search-card__actions {
        display: flex;
        align-items: center;
        gap: 8px;
        margin-top: auto;
        padding-top: 8px;
    }

    .provider-search-card__refer-btn {
        flex: 1;
        min-width: 0;
    }

    .provider-search-card__secondary-actions {
        display: flex;
        gap: 4px;
    }

    .provider-search-card__icon-btn {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 36px;
        height: 36px;
        border: 1px solid $color--border-base;
        border-radius: 6px;
        background: #fff;
        color: $color--txt-base;
        cursor: pointer;
        padding: 0;
        text-decoration: none;

        &:hover {
            background: $color--bg-dashboard;
            color: $color--txt-header;
        }
    }
</style>
